// File upload service for audio files

export interface FileUploadOptions {
  maxFileSize?: number // in bytes
  allowedTypes?: string[]
  categories?: string[]
}

export interface UploadedFile {
  file: File
  preview: string
  duration?: number
  size: number
}

class FileUploadService {
  private defaultOptions: Required<FileUploadOptions> = {
    maxFileSize: 10 * 1024 * 1024, // 10MB
    allowedTypes: [
      'audio/mpeg',
      'audio/mp3',
      'audio/wav',
      'audio/ogg',
      'audio/webm',
      'audio/mp4',
      'audio/aac',
      'audio/flac'
    ],
    categories: ['drums', 'instruments', 'effects', 'nature', 'custom']
  }

  // Validate file type and size
  validateFile(file: File, options: FileUploadOptions = {}): { valid: boolean; error?: string } {
    const opts = { ...this.defaultOptions, ...options }

    // Check if file exists and has a name
    if (!file || !file.name) {
      return {
        valid: false,
        error: 'Invalid file: no file selected'
      }
    }

    // Check file size
    if (file.size === 0) {
      return {
        valid: false,
        error: 'File is empty'
      }
    }

    if (file.size > opts.maxFileSize) {
      return {
        valid: false,
        error: `File size must be less than ${this.formatFileSize(opts.maxFileSize)}`
      }
    }

    // Check file type - be more flexible with MIME type detection
    const fileExtension = this.getFileExtension(file.name).toLowerCase()
    const allowedExtensions = ['mp3', 'wav', 'ogg', 'webm', 'mp4', 'aac', 'flac', 'm4a']

    // Check both MIME type and file extension
    const hasValidMimeType = opts.allowedTypes.includes(file.type)
    const hasValidExtension = allowedExtensions.includes(fileExtension)

    if (!hasValidMimeType && !hasValidExtension) {
      return {
        valid: false,
        error: `File type not supported. Allowed types: ${opts.allowedTypes.join(', ')} or extensions: ${allowedExtensions.join(', ')}`
      }
    }

    return { valid: true }
  }

  // Get file duration using Web Audio API
  async getAudioDuration(file: File): Promise<number> {
    return new Promise((resolve, reject) => {
      const audio = new Audio()
      const url = URL.createObjectURL(file)
      let timeoutId: NodeJS.Timeout

      // Set a timeout to prevent hanging
      timeoutId = setTimeout(() => {
        URL.revokeObjectURL(url)
        reject(new Error('Audio duration detection timed out'))
      }, 10000) // 10 second timeout

      const cleanup = () => {
        clearTimeout(timeoutId)
        URL.revokeObjectURL(url)
      }

      audio.addEventListener('loadedmetadata', () => {
        cleanup()
        // Check if duration is valid
        if (isNaN(audio.duration) || audio.duration === Infinity || audio.duration <= 0) {
          console.warn(`Invalid duration for file ${file.name}: ${audio.duration}`)
          resolve(0) // Return 0 for invalid durations instead of failing
        } else {
          resolve(audio.duration)
        }
      })

      audio.addEventListener('error', (event) => {
        cleanup()
        const error = event.target?.error
        let errorMessage = 'Failed to load audio file'

        if (error) {
          switch (error.code) {
            case error.MEDIA_ERR_ABORTED:
              errorMessage = 'Audio loading was aborted'
              break
            case error.MEDIA_ERR_NETWORK:
              errorMessage = 'Network error loading audio file'
              break
            case error.MEDIA_ERR_DECODE:
              errorMessage = 'Audio file format not supported or corrupted'
              break
            case error.MEDIA_ERR_SRC_NOT_SUPPORTED:
              errorMessage = 'Audio format not supported by browser'
              break
            default:
              errorMessage = `Audio loading error: ${error.message || 'Unknown error'}`
          }
        }

        console.warn(`Audio duration detection failed for ${file.name}: ${errorMessage}`)
        // Instead of rejecting, resolve with 0 to allow the file to be processed
        resolve(0)
      })

      // Handle cases where the audio might not trigger events
      audio.addEventListener('canplaythrough', () => {
        if (audio.duration && !isNaN(audio.duration) && audio.duration !== Infinity && audio.duration > 0) {
          cleanup()
          resolve(audio.duration)
        }
      })

      try {
        audio.src = url
        audio.load() // Explicitly load the audio
      } catch (error) {
        cleanup()
        console.warn(`Failed to set audio source for ${file.name}:`, error)
        resolve(0) // Return 0 instead of failing
      }
    })
  }

  // Create preview URL for audio file
  createPreviewURL(file: File): string {
    return URL.createObjectURL(file)
  }

  // Revoke preview URL to free memory
  revokePreviewURL(url: string): void {
    URL.revokeObjectURL(url)
  }

  // Format file size for display
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes'

    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  // Get file extension
  getFileExtension(filename: string): string {
    return filename.split('.').pop()?.toLowerCase() || ''
  }

  // Generate unique filename
  generateUniqueFilename(originalName: string): string {
    const timestamp = Date.now()
    const random = Math.random().toString(36).substr(2, 9)
    const extension = this.getFileExtension(originalName)
    return `upload_${timestamp}_${random}.${extension}`
  }

  // Convert file to blob for storage
  async fileToBlob(file: File): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        const arrayBuffer = reader.result as ArrayBuffer
        const blob = new Blob([arrayBuffer], { type: file.type })
        resolve(blob)
      }
      reader.onerror = reject
      reader.readAsArrayBuffer(file)
    })
  }

  // Get default options
  getDefaultOptions(): Required<FileUploadOptions> {
    return { ...this.defaultOptions }
  }

  // Update default options
  updateDefaultOptions(options: Partial<FileUploadOptions>): void {
    this.defaultOptions = { ...this.defaultOptions, ...options }
  }
}

// Export singleton instance
export const fileUploadService = new FileUploadService()
